import { TranslateFunc } from 'src/components/translate';
import { ShippingCollection, StockStatus } from 'src/types';

export enum ShippingType {
  CHEAPEST = 'cheapest',
  FASTEST = 'fastest',
}

export enum StockStatusEnum {
  IN_STOCK = 'in_stock',
  INCOMING = 'incoming',
  PREORDER = 'in_stock_preorder',
  UNAVAILABLE = 'not_in_stock',
  UNKNOWN = 'unknown',
}

type DeliveryDays = {
  min: number;
  max: number;
} | null;

export const getDeliveryDays = (
  type: ShippingType,
  shippingCollection?: ShippingCollection | null
): DeliveryDays => {
  const selectedShipping = shippingCollection?.[type];
  if (!shippingCollection || !selectedShipping) {
    return null;
  }

  if (
    !selectedShipping.deliveryDays ||
    selectedShipping.deliveryDays.min < 0 ||
    selectedShipping.deliveryDays.max < 0
  ) {
    return null;
  }
  return {
    min: selectedShipping.deliveryDays.min,
    max: selectedShipping.deliveryDays.max,
  };
};

const getDeliveryDaysGeneric = (
  t: TranslateFunc,
  deliveryDays: DeliveryDays
) => {
  if (!deliveryDays) {
    return undefined;
  }

  const day = t('product', `delivery-time.days-label`);
  const days = t('product', `delivery-time.days-label-plural`);

  if (deliveryDays.min === 1 && deliveryDays.max === 1) {
    return `${deliveryDays.max} ${day}`;
  }
  if (deliveryDays.min === 0 && deliveryDays.max === 0) {
    return `${deliveryDays.max} ${days}`;
  }
  if (deliveryDays.min === deliveryDays.max) {
    return `${deliveryDays.max} ${days}`;
  }
  return `${deliveryDays.min}-${deliveryDays.max} ${days}`;
};

export const isShippingInstorePickup = (
  type: ShippingType,
  shippingCollection?: ShippingCollection | null
): boolean => {
  const selectedShipping = shippingCollection?.[type];
  return Boolean(selectedShipping?.deliveryMethod === 'INSTORE_PICKUP');
};

export const getShippingDays = (
  t: TranslateFunc,
  type: ShippingType,
  shippingCollection?: ShippingCollection | null
) => {
  const deliveryDays = getDeliveryDays(type, shippingCollection);
  return getDeliveryDaysGeneric(t, deliveryDays);
};

export const getTranslatedShipping = (
  t: TranslateFunc,
  deliveryDays: DeliveryDays,
  upTo: number = 6
) => {
  if (!deliveryDays || (!deliveryDays.min && !deliveryDays.max)) {
    return t('product', `delivery-time.up-to`, { n: upTo });
  }

  return getDeliveryDaysGeneric(t, deliveryDays);
};

export const getStandardizedDeliveryText = (
  t: TranslateFunc,
  stockStatus?: StockStatus | null
) => {
  switch (stockStatus) {
    case StockStatusEnum.IN_STOCK:
      return `${t('product', `delivery-time.up-to`, { n: 6 })}`.toLowerCase();
    case StockStatusEnum.INCOMING:
      return t('product', `delivery-time.incoming`).toLowerCase();
    case StockStatusEnum.PREORDER:
      return t('product', `stock-status.in_stock_preorder`);
    case StockStatusEnum.UNAVAILABLE:
      return t('product', `delivery-time.unavailable`);
    case StockStatusEnum.UNKNOWN:
    default:
      return t('product', `delivery-time.unknown`);
  }
};

export const getDeliveryTimeText = (
  t: TranslateFunc,
  shippingDays?: string,
  stockStatus?: StockStatus | null
): string => {
  const standardizedDeliveryText = getStandardizedDeliveryText(t, stockStatus);
  const deliveryText = shippingDays || standardizedDeliveryText;

  return deliveryText;
};
